// The editor's left rail: the page's steps with a final Thank you row, then
// the sections of what is on the canvas. Rows are flat, 32px, with the
// section's own words as the label and a "..." menu that shows on hover.
// Sections drag to reorder (a 2px blue line marks the drop); the footer closes
// the list and an "Add section" row ends it. On tablets and phones the same
// rail opens in the left drawer. People who can only view get the rows
// without menus, dragging or adding.
import { useRef, useState } from 'react';
import type { Section } from '../model/pages';
import {
  ArrowDownIcon, ArrowUpIcon, CheckIcon, CopyIcon, EyeIcon, EyeOffIcon, GripIcon, MoreHorizontalIcon,
  PencilIcon, PlusIcon, TrashIcon,
} from '../icons';
import { SECTION_ICON } from './AddSection';
import { EditorMenu, type EditorMenuEntry } from './EditorMenu';
import { sectionLabel } from './sectionLabel';
import { useFlip } from './motion';

export interface EditorRailProps {
  steps: { id: string; name: string }[];
  /** Index of the step on the canvas. */
  activeStep: number;
  /** The thank-you view is on the canvas (pages only). */
  thanks: boolean;
  onSelectStep: (index: number) => void;
  onSelectThanks: () => void;
  onAddStep: () => void;
  onRenameStep: (index: number) => void;
  onMoveStep: (index: number, dir: -1 | 1) => void;
  onDuplicateStep: (index: number) => void;
  onDeleteStep: (index: number) => void;

  /** The sections on the canvas (the active step's, or the thank-you page's). */
  sections: Section[];
  /** The shared footer, shown as the last row; null when there is none. */
  footer: Section | null;
  selectedId: string | null;
  onSelectSection: (id: string) => void;
  onMoveSection: (id: string, dir: -1 | 1) => void;
  onDuplicateSection: (id: string) => void;
  onDeleteSection: (id: string) => void;
  onToggleHidden: (id: string) => void;
  onRemoveFooter: () => void;
  onAddSection: () => void;

  /** A section (or a type from the add dialog) is being dragged: rows take drops. */
  dragging: boolean;
  /** Where the drop would land (0 to sections.length), or null. */
  dropAt: number | null;
  onDragStartRow: (index: number) => void;
  onDragOverIndex: (index: number) => void;
  onDropAt: (index: number) => void;
  onDragEnd: () => void;

  /** Sections the last change touched: their rows glow once. `flashKey`
   *  changes on every new change, so the same row can glow again. */
  flashIds?: string[];
  flashKey?: number;
  /** People who can only view: rows select, nothing else. */
  readOnly?: boolean;
}

const MENU_ICON = <MoreHorizontalIcon size={15} />;

export function EditorRail(p: EditorRailProps) {
  const { steps, sections, footer, dragging, dropAt, readOnly = false } = p;
  // The row being dragged, faded while it moves (this rail's own drags only).
  const [dragId, setDragId] = useState<string | null>(null);
  // Rows glide to their new place when the order changes; new rows fade in.
  const stepsRef = useRef<HTMLOListElement>(null);
  const sectionsRef = useRef<HTMLOListElement>(null);
  useFlip(stepsRef, steps.map((st) => st.id).join(','));
  useFlip(sectionsRef, sections.map((sx) => sx.id).join(',') + (footer ? `,${footer.id}` : ''));
  const glowing = (id: string) => !!p.flashIds?.includes(id);
  const glow = (id: string) => (glowing(id) ? <span key={p.flashKey} className="pe-row-flash" aria-hidden="true" /> : null);

  function stepMenu(i: number): EditorMenuEntry[] {
    const only = steps.length <= 1;
    return [
      { label: 'Rename', icon: <PencilIcon size={15} />, onSelect: () => p.onRenameStep(i) },
      { label: 'Move up', icon: <ArrowUpIcon size={15} />, disabled: i === 0, onSelect: () => p.onMoveStep(i, -1) },
      { label: 'Move down', icon: <ArrowDownIcon size={15} />, disabled: i === steps.length - 1, onSelect: () => p.onMoveStep(i, 1) },
      { label: 'Duplicate', icon: <CopyIcon size={15} />, onSelect: () => p.onDuplicateStep(i) },
      { separator: true },
      {
        label: only ? 'A page needs at least one step' : 'Delete',
        icon: <TrashIcon size={15} />, danger: true, disabled: only, onSelect: () => p.onDeleteStep(i),
      },
    ];
  }

  function sectionMenu(sec: Section, i: number): EditorMenuEntry[] {
    return [
      { label: 'Move up', icon: <ArrowUpIcon size={15} />, disabled: i === 0, onSelect: () => p.onMoveSection(sec.id, -1) },
      { label: 'Move down', icon: <ArrowDownIcon size={15} />, disabled: i === sections.length - 1, onSelect: () => p.onMoveSection(sec.id, 1) },
      sec.hidden
        ? { label: 'Show on page', icon: <EyeIcon size={15} />, onSelect: () => p.onToggleHidden(sec.id) }
        : { label: 'Hide from page', icon: <EyeOffIcon size={15} />, onSelect: () => p.onToggleHidden(sec.id) },
      { label: 'Duplicate', icon: <CopyIcon size={15} />, onSelect: () => p.onDuplicateSection(sec.id) },
      { separator: true },
      { label: 'Delete', icon: <TrashIcon size={15} />, danger: true, onSelect: () => p.onDeleteSection(sec.id) },
    ];
  }

  const dropLine = (i: number): 'before' | 'after' | undefined => {
    if (!dragging || dropAt === null) return undefined;
    if (dropAt === i) return 'before';
    if (dropAt === sections.length && i === sections.length - 1) return 'after';
    return undefined;
  };

  return (
    <div className="pe-rail-inner">
      <section className="pe-rail-block" aria-label="Pages" data-rail-steps>
        <div className="pe-rail-head">
          <h2>Pages</h2>
          {!readOnly && (
            <button type="button" className="pe-icon-btn" aria-label="Add a step" title="Add a step" onClick={p.onAddStep}>
              <PlusIcon size={15} />
            </button>
          )}
        </div>
        <ol className="pe-list" ref={stepsRef}>
          {steps.map((st, i) => {
            const current = !p.thanks && i === p.activeStep;
            const name = st.name || `Step ${i + 1}`;
            return (
              <li key={st.id} className="pe-row" data-step-row={i} data-flip-key={st.id} data-current={current || undefined}>
                <button type="button" className="pe-page" aria-current={current ? 'step' : undefined} title={name}
                  onClick={() => { if (!current) p.onSelectStep(i); }}>
                  <span className="pe-num" aria-hidden="true">{i + 1}</span>
                  <span className="pe-label">{name}</span>
                </button>
                {!readOnly && (
                  <EditorMenu label={`Actions for ${name}`} items={stepMenu(i)} icon={MENU_ICON}
                    buttonClassName="pe-icon-btn pe-row-more" width={180} />
                )}
              </li>
            );
          })}
          <li className="pe-row" data-thanks-row data-current={p.thanks || undefined}>
            <button type="button" className="pe-page" aria-current={p.thanks ? 'step' : undefined}
              onClick={() => { if (!p.thanks) p.onSelectThanks(); }}>
              <span className="pe-num" aria-hidden="true"><CheckIcon size={11} strokeWidth={2.25} /></span>
              <span className="pe-label">Thank you</span>
            </button>
          </li>
        </ol>
      </section>

      <section className="pe-rail-block pe-rail-grow" aria-label="Sections" data-rail-sections
        onDragOver={(e) => { if (!dragging || readOnly) return; e.preventDefault(); p.onDragOverIndex(sections.length); }}
        onDrop={(e) => { if (!dragging || readOnly) return; e.preventDefault(); p.onDropAt(dropAt ?? sections.length); }}>
        <div className="pe-rail-head">
          <h2>{p.thanks ? 'Thank-you page' : 'Sections'}</h2>
          {!readOnly && (
            <button type="button" className="pe-icon-btn" aria-label="Add a section" title="Add a section" onClick={p.onAddSection}>
              <PlusIcon size={15} />
            </button>
          )}
        </div>
        <ol className="pe-list" ref={sectionsRef}>
          {sections.map((sec, i) => {
            const label = sectionLabel(sec);
            const selected = sec.id === p.selectedId;
            return (
              <li key={sec.id} className="pe-row" data-section-row={sec.id} data-flip-key={sec.id}
                data-selected={selected || undefined} data-hidden={sec.hidden || undefined}
                data-dragging={dragId === sec.id || undefined} data-drop={dropLine(i)}
                draggable={!readOnly}
                onDragStart={(e) => {
                  if (readOnly) return;
                  try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', sec.id); } catch { /* no dataTransfer (tests) */ }
                  setDragId(sec.id);
                  p.onDragStartRow(i);
                }}
                onDragEnd={() => { setDragId(null); p.onDragEnd(); }}
                onDragOver={(e) => {
                  if (!dragging) return;
                  e.preventDefault();
                  e.stopPropagation();
                  const r = e.currentTarget.getBoundingClientRect();
                  p.onDragOverIndex(e.clientY > r.top + r.height / 2 ? i + 1 : i);
                }}
                onDrop={(e) => {
                  if (!dragging) return;
                  e.preventDefault();
                  e.stopPropagation();
                  p.onDropAt(dropAt ?? i);
                }}>
                {glow(sec.id)}
                {readOnly
                  ? <span className="pe-grip-space" aria-hidden="true" />
                  : <span className="pe-grip" aria-hidden="true" title="Drag to reorder"><GripIcon size={14} /></span>}
                <button type="button" className="pe-outline-item" aria-pressed={selected} title={label}
                  onClick={() => p.onSelectSection(sec.id)}>
                  <span className="pe-outline-icon" aria-hidden="true">{SECTION_ICON[sec.type]}</span>
                  <span className="pe-label">{label}</span>
                  {sec.hidden && (
                    <>
                      <EyeOffIcon size={13} className="pe-outline-hidden" />
                      <span className="sr-only">, hidden</span>
                    </>
                  )}
                </button>
                {!readOnly && (
                  <EditorMenu label={`Actions for ${label}`} items={sectionMenu(sec, i)} icon={MENU_ICON}
                    buttonClassName="pe-icon-btn pe-row-more" width={190} />
                )}
              </li>
            );
          })}
          {footer && (
            <li className="pe-row" data-section-row={footer.id} data-flip-key={footer.id} data-footer-row data-selected={footer.id === p.selectedId || undefined}>
              {glow(footer.id)}
              <span className="pe-grip-space" aria-hidden="true" />
              <button type="button" className="pe-outline-item" aria-pressed={footer.id === p.selectedId} title={sectionLabel(footer)}
                onClick={() => p.onSelectSection(footer.id)}>
                <span className="pe-outline-icon" aria-hidden="true">{SECTION_ICON.footer}</span>
                <span className="pe-label">{sectionLabel(footer)}</span>
              </button>
              {!readOnly && (
                <EditorMenu label={`Actions for ${sectionLabel(footer)}`} icon={MENU_ICON}
                  buttonClassName="pe-icon-btn pe-row-more" width={190}
                  items={[{ label: 'Remove footer', icon: <TrashIcon size={15} />, danger: true, onSelect: p.onRemoveFooter }]} />
              )}
            </li>
          )}
        </ol>
        {!readOnly && (
          <button type="button" className="pe-add-row" onClick={p.onAddSection}>
            <PlusIcon size={15} />Add section
          </button>
        )}
      </section>
    </div>
  );
}
